import { FunctionComponent, useState } from "react";
import { connect } from "react-redux";
import { themeColors } from "../../theme";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import InputTextValue from "../../TextInput/InputTextValue";

// Required for React-Modal (prevents accessibility warnings)
Modal.setAppElement("#root");

/* ------------------------------------------------------------------
   PROPS
-------------------------------------------------------------------*/
type props = {
  show: boolean;                 // Whether modal is visible
  heading: string;               // Main modal title
  subHeading: string;            // Label for the input field
  close: () => void;             // Close callback
  onSave: (v: any) => void;   // Save callback
  theme?: any;               // Light/Dark mode flag
};

/* ------------------------------------------------------------------
   COMPONENT
-------------------------------------------------------------------*/
const AddModal: FunctionComponent<props> = ({
  show,
  heading,
  subHeading,
  close,
  onSave,
  theme
}) => {
  const [value, setValue] = useState("");

  // Responsive utilities
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /* ------------------------------------------------------------------
   MODAL STYLES (React-Modal inline styling)
   - Makes modal centered
   - Applies dark/light theme colors
-------------------------------------------------------------------*/
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /* ------------------------------------------------------------------
   RENDER
-------------------------------------------------------------------*/
  return (
    <Modal
      isOpen={show}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >

      {/* FORM WRAPPER */}
      <form style={{ width: "100%" }}>

        {/* Modal Heading */}
        <Box sx={{ mb: 2 }}>
          <h3
            style={{
              color: theme ? "#272944" : "#fff",
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              marginBottom: 24,
              textAlign: "center",
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            {heading}
          </h3>
        </Box>

        {/* Input Field */}
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid item xs={12}>
            <InputTextValue
              title={subHeading}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              width: "100%",
            }}
          >

            {/* Cancel Button */}
            <Box style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}
                isMobile={isMobile}
              >
                Cancel
              </StyledCancelButton>
            </Box>

            {/* Submit Button */}
            <Box>
              <StyledSubmitButton
                onClick={() => onSave(value)}
                themeMode={themeMUI.palette.mode}
                isMobile={isMobile}
              >
                Submit
              </StyledSubmitButton>
            </Box>
          </Box>
        </Grid>
      </form>
    </Modal>
  );
};

/* ------------------------------------------------------------------
   REDUX: MAP STATE TO PROPS
-------------------------------------------------------------------*/
const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode
  };
};
export default connect(mapStateToProps)(AddModal);
